import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const empty = { name: "", company_id: "", designation: "", phone: "", email: "", whatsapp: "", contact_type: "Other", notes: "" };

function Field({ label, required, children, full }) {
  return (
    <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}>
      <Label className="text-xs font-semibold text-slate-600">{label}{required && <span className="text-red-500"> *</span>}</Label>
      {children}
    </div>
  );
}

export function ContactFormDialog({ open, onOpenChange, contact, onSaved }) {
  const { meta } = useAuth();
  const [form, setForm] = useState(empty);
  const [companies, setCompanies] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const editing = !!contact;

  useEffect(() => {
    if (open) {
      setErr("");
      setForm(contact ? { ...empty, ...contact, company_id: contact.company_id || "" } : empty);
      api.get("/companies?limit=200").then((r) => setCompanies(r.data.items)).catch(() => {});
    }
  }, [open, contact]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { setErr("Name is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, company_id: form.company_id || null };
      if (editing) { await api.put(`/contacts/${contact.id}`, payload); toast.success("Contact updated"); }
      else { await api.post("/contacts", payload); toast.success("Contact added"); }
      onOpenChange(false); onSaved && onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl gap-0 p-0 max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader className="border-b border-slate-100 px-6 py-4"><DialogTitle className="text-lg">{editing ? "Edit Contact" : "Add Contact"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-4 overflow-y-auto px-6 py-5 sm:grid-cols-2">
          <Field label="Full Name" required full>
            <Input data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} />
            {err && <p className="text-xs text-red-500">{err}</p>}
          </Field>
          <Field label="Company" full>
            <Select value={form.company_id || "none"} onValueChange={(v) => set("company_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="contact-company"><SelectValue placeholder="Select company" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not linked</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Designation"><Input data-testid="contact-designation" value={form.designation} onChange={(e) => set("designation", e.target.value)} /></Field>
          <Field label="Contact Type">
            <Select value={form.contact_type} onValueChange={(v) => set("contact_type", v)}>
              <SelectTrigger data-testid="contact-type"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta?.contact_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Phone"><Input data-testid="contact-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 …" /></Field>
          <Field label="WhatsApp"><Input data-testid="contact-whatsapp" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+91 …" /></Field>
          <Field label="Email" full><Input data-testid="contact-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
          <Field label="Notes" full><Textarea data-testid="contact-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="contact-submit" onClick={submit} disabled={saving} className="font-semibold">{saving ? "Saving…" : editing ? "Save" : "Add Contact"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
